import * as Yup from 'yup';
import { useMemo, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { Draggable, Droppable, DropResult, DragDropContext } from '@hello-pangea/dnd';

import { LoadingButton } from '@mui/lab';
import { Box, Stack } from '@mui/system';
import {
  Card,
  Button,
  useTheme,
  CardHeader,
  Typography,
  IconButton,
  CardContent
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { OrganizationReason, OrganizationReasonType } from 'src/services/organisation/organisation.types';
import {
  useGetOrganizationReasonsQuery,
  useUpdateOrganizationReasonsMutation
} from 'src/services/organisation/organisation.service';

import Iconify from 'src/components/iconify';
import { RHFTextField } from 'src/components/hook-form';

import { BusinessErrorCodes } from 'src/types/business-errors';

// ----------------------------------------------------------------------

/** Mirrors MAX_ORGANIZATION_REASONS on the server. The server is what enforces it. */
export const MAX_ORGANIZATION_REASONS = 10;

const MAX_LABEL_LENGTH = 100;

const CARD: Record<OrganizationReasonType, { copyKey: string; icon: string }> = {
  [OrganizationReasonType.ApplicationWithdrawal]: { copyKey: 'withdrawal_reasons', icon: 'mdi:account-arrow-left-outline' },
  [OrganizationReasonType.JobClose]: { copyKey: 'job_close_reasons', icon: 'mdi:briefcase-off-outline' },
};

// `reason_id`, not `id`: useFieldArray keeps its own `id` on every row.
const toFormRows = (reasons?: OrganizationReason[]) =>
  (reasons ?? []).map((reason) => ({ reason_id: reason.id, label: reason.label }));

type Props = {
  type: OrganizationReasonType;
};

/**
 * One of an organisation's reason lists (JT1-1800), edited as a whole: rename, add, reorder by
 * dragging, remove. The server archives removed reasons, so records that used one keep it.
 */
export default function OrganizationReasonsSettings({ type }: Props) {
  const { t } = useTranslate();
  const theme = useTheme();

  const { organization, account } = useAuthContext();

  const { copyKey, icon } = CARD[type];

  const { currentData: reasons, isLoading } = useGetOrganizationReasonsQuery({ org_id: organization.id, type });

  const [updateReasons, { isLoading: isSubmitting }] = useUpdateOrganizationReasonsMutation();

  const ReasonsSchema = Yup.object().shape({
    reasons: Yup.array()
      .of(
        Yup.object().shape({
          reason_id: Yup.string(),
          label: Yup.string()
            .trim()
            .required(t('validation.required'))
            .max(MAX_LABEL_LENGTH, t('validation.too_long')),
        })
      )
      .unique(t('admin-organization.settings.organization_reasons.errors.duplicate'), (value: any) => value.label?.trim().toLowerCase())
      .min(1, t('validation.array_min', { length: 1 }))
      .max(MAX_ORGANIZATION_REASONS, t('validation.array_max', { length: MAX_ORGANIZATION_REASONS }))
      .required(t('validation.required'))
  });

  const defaultValues = useMemo(() => ({ reasons: toFormRows(reasons) }), [reasons]);

  const methods = useForm({
    resolver: yupResolver(ReasonsSchema),
    defaultValues,
    mode: 'all',
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = methods;

  useEffect(() => {
    if (reasons) {
      reset({ reasons: toFormRows(reasons) });
    }
  }, [reasons, reset]);

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'reasons',
  });

  const canEditSettings = useMemo(() => {
    if (!account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(account.org_role);
  }, [account]);

  const remaining = MAX_ORGANIZATION_REASONS - fields.length;

  const onDragEnd = ({ source, destination }: DropResult) => {
    if (!destination || source.index === destination.index) return;

    move(source.index, destination.index);
  };

  const errorMessageFor = (error: any): string => {
    if (error?.data?.error_code === BusinessErrorCodes.DuplicateOrganizationReason) return t('admin-organization.settings.organization_reasons.errors.duplicate');
    if (error?.data?.error_code === BusinessErrorCodes.OrganizationReasonLimitReached) return t('admin-organization.settings.organization_reasons.errors.limit');
    return t('admin-organization.settings.organization_reasons.api.update.default_error');
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updateReasons({
        org_id: organization.id,
        type,
        reasons: data.reasons.map((row) => ({
          id: row.reason_id || undefined,
          label: row.label.trim(),
        })),
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.organization_reasons.api.update.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(errorMessageFor(error), { variant: 'error' });
    }
  });

  if (isLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Card>
          <CardContent>
            <Typography>{t('common.loading')}</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      <Card
        sx={{
          width: '100%',
          position: 'relative',
          boxShadow: 0
        }}
      >
        <CardHeader
          avatar={
            <Iconify
              icon={icon}
              sx={{
                width: 30,
                height: 30,
                color: theme.palette.primary.main,
              }}
            />
          }
          title={
            <Typography variant="h6" lineHeight={1.75}>
              {t(`admin-organization.settings.${copyKey}.title`)}
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="textSecondary">
              {t(`admin-organization.settings.${copyKey}.description`)}
            </Typography>
          }
        />

        <CardContent>
          <FormProvider {...methods}>
            <Stack direction="column" sx={{ mt: 2 }} gap={2}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId={`reasons-${type}`}>
                  {(provided) => (
                    <Stack spacing={2} ref={provided.innerRef} {...provided.droppableProps}>
                      {fields.map((field, index) => (
                        <Draggable
                          key={field.id}
                          draggableId={field.id}
                          index={index}
                          isDragDisabled={!canEditSettings}
                        >
                          {(draggable) => (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              ref={draggable.innerRef}
                              {...draggable.draggableProps}
                            >
                              <Box
                                {...draggable.dragHandleProps}
                                sx={{
                                  display: 'flex',
                                  color: 'text.disabled',
                                  cursor: canEditSettings ? 'grab' : 'default'
                                }}
                              >
                                <Iconify icon="mdi:drag-vertical" />
                              </Box>

                              <RHFTextField
                                name={`reasons.${index}.label`}
                                label={t('admin-organization.settings.organization_reasons.label')}
                                disabled={!canEditSettings}
                                size="small"
                                fullWidth
                              />

                              <IconButton
                                onClick={() => remove(index)}
                                disabled={!canEditSettings || fields.length <= 1}
                                aria-label={t('admin-organization.settings.organization_reasons.remove')}
                              >
                                <Iconify icon="eva:trash-2-outline" />
                              </IconButton>
                            </Stack>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Stack>
                  )}
                </Droppable>
              </DragDropContext>

              {errors.reasons?.root && (
                <Typography variant="caption" color="error">
                  {errors.reasons.root.message}
                </Typography>
              )}

              {errors.reasons?.message && (
                <Typography variant="caption" color="error">
                  {errors.reasons.message}
                </Typography>
              )}

              {canEditSettings && (
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Button
                    startIcon={<Iconify icon="eva:plus-fill" />}
                    onClick={() => append({ reason_id: '', label: '' })}
                    disabled={remaining <= 0}
                  >
                    {t('admin-organization.settings.organization_reasons.add')}
                  </Button>

                  <Typography variant="caption" color="textSecondary">
                    {remaining > 0
                      ? t('admin-organization.settings.organization_reasons.remaining', { count: remaining })
                      : t('admin-organization.settings.organization_reasons.limit_reached')}
                  </Typography>
                </Stack>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <LoadingButton
                  type="submit"
                  onClick={onSubmit}
                  variant="contained"
                  loading={isSubmitting}
                  disabled={!isValid || !canEditSettings}
                >
                  {t('common.update')}
                </LoadingButton>
              </Box>
            </Stack>
          </FormProvider>
        </CardContent>
      </Card>
    </Box>
  );
}
